import React from 'react';
import { Box, Typography, Button, useTheme } from '@mui/material';
import { AddAPhoto as PhotoIcon, CameraAlt as CameraIcon, CloudUpload as UploadIcon } from '@mui/icons-material';

type DocType = 'receipt' | 'invoice';

interface UploadCaptureControlsProps {
  docType: DocType;
  file: File | null;
  isDragActive: boolean;
  getRootProps: () => any;
  getInputProps: () => any;
  cameraInputRef: React.RefObject<HTMLInputElement | null>;
  onCameraCapture: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

// Shared by UploadProofDialog and AddCashTransactionDialog — every place a receipt/invoice can be
// captured fresh (take a photo, choose from the photo library, or drag & drop/click to pick a
// file). Each caller owns its own useDropzone()/camera <input> ref and upload logic; this is
// deliberately presentational only, so the two dialogs' very different post-upload flows (link
// to an existing bank transaction vs. confirm as a brand-new cash one) stay out of it.
const UploadCaptureControls: React.FC<UploadCaptureControlsProps> = ({ docType, file, isDragActive, getRootProps, getInputProps, cameraInputRef, onCameraCapture }) => {
  const { palette } = useTheme();
  return (
    <>
      {docType === 'receipt' && (
        <>
          <Button
            fullWidth
            variant="contained"
            startIcon={<CameraIcon />}
            onClick={() => cameraInputRef.current?.click()}
            sx={{ borderRadius: 2, py: 1, mb: 1.5 }}
          >
            Take Photo
          </Button>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={onCameraCapture}
          />
        </>
      )}

      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : palette.tokens.hairline,
          borderRadius: 2, p: 2.5, textAlign: 'center', cursor: 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'transparent',
          '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
        }}
      >
        <input {...getInputProps()} />
        {docType === 'receipt'
          ? <PhotoIcon sx={{ fontSize: 28, color: palette.tokens.mutedSoft, mb: 0.5 }} />
          : <UploadIcon sx={{ fontSize: 28, color: palette.tokens.mutedSoft, mb: 0.5 }} />}
        <Typography variant="body2" sx={{ fontWeight: 600, color: palette.tokens.ink }}>
          {file ? file.name : (isDragActive ? 'Drop it here' : docType === 'receipt' ? 'Choose from your photo library' : 'Choose a PDF')}
        </Typography>
        <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>
          {docType === 'receipt' ? 'or drag & drop — JPEG, PNG, WebP, HEIC' : 'or drag & drop'}
        </Typography>
      </Box>
    </>
  );
};

export default UploadCaptureControls;
